import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { navigate, tasksVersion } from '../store.js';
import { __, _n } from '../i18n.js';
import Header from '../components/Header.jsx';
import Icon from '../components/Icon.jsx';
import ScheduleTodayCard from '../components/ScheduleTodayCard.jsx';
import WarningsCard from '../components/WarningsCard.jsx';
import ShoppingCard from '../components/ShoppingCard.jsx';
import InstallCard from '../components/InstallCard.jsx';

/**
 * HH-1: "Vandaag" — the dashboard that becomes the hub's glue (plan §3).
 *
 * HH-7 filled in the three cards HH-1 left as placeholders: today's
 * weekritme grouped per gezinslid, the warnings the server already
 * decided about, and the boodschappen badge.
 *
 * Each card is its own component with its own fetch, deliberately — that
 * is what makes "a failing source hides one card and leaves the rest of
 * the dashboard working" (acceptance criterion 1) structural rather than
 * something this screen has to remember. It also means the hero below
 * never waits on any of them.
 *
 * The hero keeps WP-3's behaviour exactly: one action into the
 * category→time→task flow, and an open-task count that fails silently
 * (a failed count means no line, never an error toast on the landing
 * screen). The FAB and the desktop `n` shortcut remain the only add-task
 * entry points here (WP-7 brief 7c).
 */

export default function VandaagScreen() {
  const [openCount, setOpenCount] = useState(null);
  const version = tasksVersion.value; // re-fetch the count after any task mutation

  useEffect(() => {
    let cancelled = false;
    api.tasks
      .list({ status: 'active' })
      .then((res) => {
        if (!cancelled) setOpenCount((res.tasks || []).length);
      })
      .catch(() => {
        if (!cancelled) setOpenCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  return (
    <div class="etm-home">
      <Header
        title="ETM"
        actions={
          <button
            type="button"
            class="etm-icon-btn"
            aria-label={__('Instellingen', 'etm')}
            onClick={() => navigate('settings')}
          >
            <Icon name="settings" size={24} />
          </button>
        }
      />

      <div class="etm-home__hero">
        <h1 class="etm-screen-title">{__('Aan de slag', 'etm')}</h1>
        <p class="etm-screen-subtitle">
          {openCount === null
            ? __('ETM kiest de taak die nu past.', 'etm')
            : openCount === 0
              ? __('Geen openstaande taken.', 'etm')
              : _n('%d openstaande taak', '%d openstaande taken', openCount, 'etm').replace('%d', openCount)}
        </p>

        <button type="button" class="etm-btn etm-btn-primary etm-btn-hero" onClick={() => navigate('category')}>
          <Icon name="rocket_launch" size={28} /> {__('Start taak', 'etm')}
        </button>
      </div>

      {/* Order is the brief's: today's fixed commitments first (you
          cannot move them), then what needs attention, then the shopping
          badge. Every one of them renders nothing at all when it has
          nothing to say or its fetch failed. */}
      <div class="etm-dashboard-cards">
        {/* First, and only ever visible while the app is NOT installed. */}
        <InstallCard />
        <ScheduleTodayCard />
        <WarningsCard />
        <ShoppingCard />
      </div>
    </div>
  );
}
